import { MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  repeat,
  RepeatConfig,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ArrayFunctions } from './array.functions';
import { ObjectFunctions } from './object.functions';

interface CacheEntry<V> {
  value$: Observable<V>;
  lastValue?: V;
  hasValue: boolean; // false until the loader has emitted at least once
}

export class ObservableFunctions {
  /**
   * Wrap a key-based loader so repeated calls with the same key return one shared,
   * replayed observable instead of re-invoking the loader.
   *
   * The first call per key runs `loader(key)` and caches the resulting stream
   * (shareReplay, no refCount) so the value survives with no active subscribers.
   * An optional `shouldBust` predicate is evaluated per call against the last value
   * the loader emitted for that key; returning `true` re-runs the loader. Errors are
   * not cached — a failed stream is evicted so the next call retries.
   *
   * Intended for bounded key spaces (e.g. entity ids): the cache keeps one entry per
   * distinct key for the app session with no eviction, so avoid high-cardinality keys.
   */
  public static cacheByKey<K extends string | number, V>(
    loader: (key: K) => Observable<V>
  ): (key: K, shouldBust?: (cachedValue: V) => boolean) => Observable<V> {
    const cache = new Map<K, CacheEntry<V>>();

    return (key: K, shouldBust?: (cachedValue: V) => boolean): Observable<V> => {
      const cached = cache.get(key);
      const bust = !!cached && cached.hasValue && !!shouldBust && shouldBust(cached.lastValue as V);

      if (cached && !bust) {
        return cached.value$;
      }

      const entry: CacheEntry<V> = { hasValue: false, value$: null as unknown as Observable<V> };
      entry.value$ = loader(key).pipe(
        tap((value) => {
          entry.lastValue = value;
          entry.hasValue = true;
        }),
        catchError((err) => {
          if (cache.get(key) === entry) {
            cache.delete(key);
          }
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

      cache.set(key, entry);
      return entry.value$;
    };
  }

  public static distinctUntilSomeKeyChanged<T extends object>() {
    return distinctUntilChanged<T>(ObjectFunctions.deepEquals);
  }

  public static repeatUntil<T>(
    until: (value: T) => boolean,
    repeatConfig: number[] | number | RepeatConfig
  ): MonoTypeOperatorFunction<T> {
    const SECOND = 1000;

    const config = Array.isArray(repeatConfig)
      ? {
          count: repeatConfig.length + 1,
          delay: (retryCount) => timer(repeatConfig[retryCount - 1] * SECOND),
        }
      : repeatConfig;

    return (source$: Observable<T>) =>
      source$.pipe(
        repeat(config),
        first((data) => until(data))
      );
  }

  public static confirm<T>(confirmFn: (s?: T) => Observable<boolean>) {
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        switchMap((s) =>
          confirmFn(s).pipe(
            take(1),
            filter((confirmed) => !!confirmed)
          )
        ),
        withLatestFrom(source$),
        map(([_, source]) => source)
      );
  }

  public static on<T extends string | { type: string } = string>(events: string | string[]) {
    const eventsArr = ArrayFunctions.wrapIntoArray(events);
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        filter((s) =>
          eventsArr.some((event) => {
            if (typeof s === 'string') return event === s;
            return event === s.type;
          })
        )
      );
  }
}
